import { buttonClasses } from '@mui/material/Button';
import MuiMenu, { type MenuProps } from '@mui/material/Menu';
import { type SxProps, type Theme, useTheme } from '@mui/material/styles';
import { Children, cloneElement, isValidElement, type ReactElement, type ReactNode, useId, useState } from 'react';

type MenuButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  sx?: SxProps<Theme>;
};

type MenuChildProps = {
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
};

type Props = {
  open?: boolean;
  setOpen?: (newState: boolean) => void;
  button: ReactElement<MenuButtonProps>;
  children: ReactNode;
  equalMenuWidth?: boolean;
  'data-testid'?: string;
} & Omit<MenuProps, 'open' | 'anchorEl' | 'id' | 'children' | 'sx'>;

export const Menu = ({
  open: controlledOpen,
  setOpen: setControlledOpen,
  button,
  children,
  equalMenuWidth = false,
  'data-testid': testId,
  ...rest
}: Props) => {
  const menuButtonId = useId();
  const menuId = useId();
  const theme = useTheme();
  const [internalOpen, setInternalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isControlled = controlledOpen !== undefined && setControlledOpen !== undefined;

  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? setControlledOpen : setInternalOpen;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (button.props.onClick) {
      button.props.onClick(event);
    }
    handleClick(event);
  };

  const defaultButtonSx = {
    '&[aria-expanded="true"]': {
      borderColor: theme.palette.primary.main,
    },
    [`&:${buttonClasses.text}`]: {
      border: `1px solid transparent`,
    },
  };

  const combinedButtonSx = Array.isArray(button.props.sx)
    ? [defaultButtonSx, ...button.props.sx]
    : button.props.sx
      ? [defaultButtonSx, button.props.sx]
      : defaultButtonSx;

  const menuButton = cloneElement(button, {
    ...button.props,
    'aria-controls': open ? menuId : undefined,
    'aria-expanded': open ? 'true' : undefined,
    'aria-haspopup': 'true',
    id: menuButtonId,
    onClick: onButtonClick,
    sx: combinedButtonSx,
  });

  const menuItems = Children.map(children, (child) => {
    if (!isValidElement<MenuChildProps>(child)) {
      return null;
    }

    const onClick = (event: React.MouseEvent<HTMLElement>) => {
      if (child.props.onClick) {
        child.props.onClick(event);
      }
      setOpen(false);
    };

    return cloneElement(child, { onClick });
  });

  return (
    <div>
      {menuButton}
      <MuiMenu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        data-testid={testId}
        slotProps={{
          ...rest.slotProps,
          list: {
            ...rest.slotProps?.list,
            'aria-labelledby': menuButtonId,
            sx: {
              ...(typeof rest.slotProps?.list === 'object' ? rest.slotProps.list.sx : {}),
              minWidth: 200,
              width: equalMenuWidth && anchorEl ? anchorEl.offsetWidth : undefined, // makes menu items the same width as the button
            },
          },
        }}
        {...rest}
      >
        {menuItems}
      </MuiMenu>
    </div>
  );
};
